const cards = [
  {
    title: 'Clean Architecture',
    subtitle: 'Scalable and maintainable foundations',
    description:
      'Systems are designed with a clear separation of concerns, modular component structures, and well-defined interfaces that enable long-term maintainability and effortless evolution as requirements grow.',
    imageSrc:
      'https://readdy.ai/api/search-image?query=Abstract%20geometric%20composition%20representing%20software%20architecture%20through%20clean%20layered%20rectangular%20blocks%20and%20connecting%20fine%20lines%2C%20soft%20warm%20beige%20background%20with%20subtle%20texture%2C%20structured%20grid%20pattern%20suggesting%20systematic%20organization%2C%20minimal%20and%20precise%20linework%2C%20no%20text%20no%20people%20no%20recognizable%20objects%2C%20editorial%20abstract%20art%20quality%2C%20soft%20diffused%20lighting%2C%20warm%20neutral%20palette%20with%20cream%20and%20beige%20tones%2C%20balanced%20structured%20composition%2C%20architectural%20blueprint%20aesthetic%2C%20high%20resolution%20geometric%20abstraction&width=500&height=400&seq=advnet-eng-arch&orientation=landscape',
    imageAlt: 'Abstract layered rectangular blocks representing software architecture',
    isDark: false,
    tag: 'Architecture',
  },
  {
    title: 'Performance Optimization',
    subtitle: 'Efficiency at every layer',
    description:
      'Every component is optimized for speed and resource efficiency through careful profiling, algorithmic refinement, and a deep understanding of the iOS platform capabilities and constraints.',
    imageSrc:
      'https://readdy.ai/api/search-image?query=Abstract%20geometric%20composition%20with%20a%20bold%20striking%20geometric%20form%20centered%20on%20deep%20charcoal%20background%2C%20single%20luminous%20warm%20golden%20amber%20accent%20shape%20radiating%20energy%20and%20precision%2C%20subtle%20fine%20line%20patterns%20creating%20technical%20sophistication%2C%20dark%20sophisticated%20palette%20with%20one%20bright%20focal%20element%2C%20no%20text%20no%20people%20no%20products%2C%20dramatic%20editorial%20abstract%20art%2C%20high%20contrast%20lighting%2C%20centered%20powerful%20composition%2C%20modern%20minimalist%20aesthetic%20with%20technical%20precision%2C%20high%20resolution%20dramatic%20geometric%20abstraction&width=500&height=400&seq=advnet-eng-perf&orientation=landscape',
    imageAlt: 'Bold geometric form with golden accent on dark background',
    isDark: true,
    tag: 'Performance',
  },
  {
    title: 'User-Centered Design',
    subtitle: 'Intuitive and thoughtful experiences',
    description:
      'Design decisions are grounded in usability principles and real user needs. Interfaces are crafted to be intuitive, accessible, and thoughtfully structured to support natural interaction patterns on the iOS platform.',
    imageSrc:
      'https://readdy.ai/api/search-image?query=Abstract%20geometric%20composition%20with%20smooth%20flowing%20rounded%20shapes%20and%20gentle%20curved%20lines%20suggesting%20intuitive%20design%20and%20user%20flow%2C%20soft%20warm%20cream%20background%20with%20delicate%20texture%2C%20harmonious%20balanced%20arrangement%20of%20circular%20and%20rounded%20rectangle%20elements%2C%20minimal%20clean%20aesthetic%2C%20no%20text%20no%20people%20no%20recognizable%20objects%2C%20editorial%20abstract%20art%20quality%2C%20soft%20diffused%20lighting%2C%20warm%20neutral%20palette%2C%20organic%20geometric%20precision%2C%20flowing%20visual%20rhythm%2C%20high%20resolution%20geometric%20abstraction&width=500&height=400&seq=advnet-eng-usab&orientation=landscape',
    imageAlt: 'Flowing rounded geometric shapes suggesting intuitive design',
    isDark: false,
    tag: 'Usability',
  },
];

export default function EngineeringApproach() {
  return (
    <section className="relative bg-background-50 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 mb-16">
          <div className="lg:w-2/5">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground-500 font-heading">
              Engineering
            </h2>
            <h3 className="text-3xl md:text-4xl font-bold text-foreground-950 italic font-heading mt-1">
              Approach
            </h3>
          </div>
          <div className="lg:w-3/5">
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed font-body">
              The team follows modern software engineering practices, including clean architecture,
              performance optimization, and user-centered design. Development workflows are
              continuously refined to adopt up-to-date engineering standards and deliver reliable,
              future-ready IT solutions.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {cards.map((card) => (
            <article
              key={card.tag}
              className="rounded-2xl overflow-hidden bg-background-100 flex flex-col h-full"
            >
              <div className={`relative h-[260px] md:h-[300px] overflow-hidden ${card.isDark ? 'bg-primary-900' : 'bg-background-200'}`}>
                <img
                  src={card.imageSrc}
                  alt={card.imageAlt}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <div className="p-5 md:p-6 flex flex-col flex-1">
                <span className={`inline-block text-xs font-semibold uppercase tracking-wider rounded-lg px-2.5 py-1 mb-4 whitespace-nowrap font-label ${
                  card.isDark
                    ? 'bg-accent-100 text-accent-800'
                    : 'bg-accent-100 text-accent-800'
                }`}>
                  {card.tag}
                </span>
                <h4 className="text-xl md:text-2xl font-bold text-foreground-950 leading-snug mb-3 font-heading">
                  {card.title}
                </h4>
                <p className="text-sm text-foreground-500 leading-relaxed mb-2 font-body">
                  {card.subtitle}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mt-auto font-body">
                  {card.description}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}